import { useI18n } from '@change-corgi/core-react/i18n';

import type { DecisionMaker } from '../../types';
import { DecisionMakerCard } from '../DecisionMakerCard';

import { DecisionMakerNavigation } from './components/DecisionMakerNavigation';
import { useDecisionMakerCarousel } from './hooks/useDecisionMakerCarousel';

/** The decision-maker cards, one per view: swipe or use the arrows. */
export const DecisionMakerCarousel: React.FC<Props> = ({ decisionMakers, onRemove, headingId }) => {
	const { translate } = useI18n();
	const { data, actions } = useDecisionMakerCarousel({ total: decisionMakers.length });

	return (
		<section
			aria-roledescription={translate('corgi.start_a_petition.decision_makers.carousel_roledescription')}
			aria-labelledby={headingId}
			className="relative flex flex-col gap-2"
		>
			{/* Safari drops list semantics from an unstyled list, so the roles are explicit */}
			<ul
				ref={data.listRef}
				role="list"
				className="flex snap-x snap-mandatory [scrollbar-width:none] gap-4 overflow-x-auto overscroll-x-contain motion-safe:scroll-smooth [&::-webkit-scrollbar]:hidden"
			>
				{decisionMakers.map((decisionMaker, index) => (
					<li
						key={decisionMaker.canonicalPetitionTargetId}
						role="listitem"
						id={`${data.slideIdPrefix}-${index}`}
						aria-roledescription={translate('corgi.start_a_petition.decision_makers.slide_roledescription')}
						aria-label={translate('corgi.start_a_petition.decision_makers.navigation.counter', {
							current: index + 1,
							total: decisionMakers.length,
						})}
						// the section's focus fallback lands here after a Remove
						data-focus-target={index === data.currentIndex ? 'decision-maker-current-slide' : undefined}
						// only the card in view can be reached by keyboard or assistive tech
						inert={index !== data.currentIndex}
						className="focus-visible:outline-stroke-focus flex w-full shrink-0 snap-start [scroll-snap-stop:always] rounded-lg focus-visible:outline-2 focus-visible:-outline-offset-2 [&>*]:flex-auto"
					>
						<DecisionMakerCard
							decisionMaker={decisionMaker}
							onRemove={onRemove && (() => onRemove(decisionMaker.canonicalPetitionTargetId))}
						/>
					</li>
				))}
			</ul>
			{data.navigationShown && (
				<DecisionMakerNavigation
					position={data.currentIndex + 1}
					total={decisionMakers.length}
					previousDisabled={data.previousDisabled}
					nextDisabled={data.nextDisabled}
					onPrevious={actions.showPrevious}
					onNext={actions.showNext}
				/>
			)}
		</section>
	);
};

type Props = {
	decisionMakers: readonly DecisionMaker[];
	/** Omit for read-only cards. */
	onRemove?: (canonicalPetitionTargetId: string) => void;
	/** The id of the section heading, which names the carousel. */
	headingId: string;
};
